import { CopyOutlined, DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Spin, Tooltip, message } from "antd";
import PropTypes from "prop-types";
import { Component } from "react";

const contentStyles = `
.gap-prediction-content {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
}
.gap-prediction-content h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #1a1a2e;
}
.gap-prediction-content p {
    margin-bottom: 12px;
    color: #555;
}
.gap-prediction-content ul {
    padding-left: 20px;
    margin-bottom: 12px;
}
.gap-prediction-content li {
    margin-bottom: 8px;
}
.gap-prediction-content strong {
    color: #1a1a2e;
}
.gap-prediction-content code {
    font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
    font-size: 13px;
    background-color: #f0f0f5;
    padding: 2px 6px;
    border-radius: 4px;
    color: #d63384;
    font-weight: 500;
}
`;

class GapPredictionModal extends Component {
    extractTextFromHtml = (html) => {
        if (!html) return "";
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return doc.body.textContent || "";
    };

    handleCopyText = () => {
        const { gapPredictionData } = this.props;
        const predictionHtml = gapPredictionData?.predictionHtml;
        if (predictionHtml) {
            const textContent = this.extractTextFromHtml(predictionHtml);
            navigator.clipboard
                .writeText(textContent)
                .then(() => {
                    message.success("Gap prediction copied to clipboard");
                })
                .catch(() => {
                    message.error("Failed to copy to clipboard");
                });
        }
    };

    buildPrintSafeHtml = (html) => {
        if (!html) return html;
        return html.replace(
            "</head>",
            "<style>html,body{overflow-x:hidden;max-width:100%;word-wrap:break-word}table{table-layout:fixed;width:100%;word-break:break-word}td,th{overflow-wrap:break-word}</style></head>",
        );
    };

    handleDownloadPdf = () => {
        const { gapPredictionData } = this.props;
        const predictionHtml = gapPredictionData?.predictionHtml;
        if (!predictionHtml) return;

        const printWindow = window.open("", "_blank");
        if (!printWindow) return;

        printWindow.document.write(this.buildPrintSafeHtml(predictionHtml));
        printWindow.document.close();

        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
            }, 300);
        };
    };

    render() {
        const { visible, onClose, generateGapPredictionResult, gapPredictionData, onRegenerate } = this.props;
        const isLoading = generateGapPredictionResult?.loading;
        const hasError = generateGapPredictionResult && !generateGapPredictionResult.success && !generateGapPredictionResult.loading;
        const errorMessage = generateGapPredictionResult?.message;
        const predictionHtml = gapPredictionData?.predictionHtml;

        return (
            <Modal
                title="✨ Architectural Gap Prediction"
                open={visible}
                onCancel={onClose}
                width={720}
                destroyOnClose
                footer={[
                    <Button key="regenerate" onClick={onRegenerate} disabled={isLoading}>
                        <ReloadOutlined />
                        Regenerate
                    </Button>,
                    predictionHtml && !isLoading && !hasError && (
                        <Tooltip title="Copy gap prediction to clipboard" key="copy">
                            <Button icon={<CopyOutlined />} onClick={this.handleCopyText}>
                                Copy
                            </Button>
                        </Tooltip>
                    ),
                    predictionHtml && !isLoading && !hasError && (
                        <Tooltip title="Download as PDF" key="download">
                            <Button icon={<DownloadOutlined />} onClick={this.handleDownloadPdf}>
                                Download PDF
                            </Button>
                        </Tooltip>
                    ),
                    <Button key="done" type="primary" onClick={onClose}>
                        Done
                    </Button>,
                ]}
            >
                {isLoading && (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16, color: "#888" }}>Analyzing schema for architectural gaps...</p>
                    </div>
                )}

                {hasError && (
                    <Alert
                        type="error"
                        showIcon
                        message="Failed to generate gap prediction"
                        description={errorMessage || "An unexpected error occurred. Please try again."}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!isLoading && predictionHtml && (
                    <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        <style>{contentStyles}</style>
                        <div className="gap-prediction-content" dangerouslySetInnerHTML={{ __html: this.buildPrintSafeHtml(predictionHtml) }} />
                    </div>
                )}

                {!isLoading && !hasError && !predictionHtml && <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>No gap prediction data available.</div>}
            </Modal>
        );
    }
}

GapPredictionModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    generateGapPredictionResult: PropTypes.shape({
        success: PropTypes.bool,
        loading: PropTypes.bool,
        message: PropTypes.string,
    }),
    gapPredictionData: PropTypes.shape({
        schemaId: PropTypes.string,
        businessAreaId: PropTypes.string,
        schemaName: PropTypes.string,
        predictionHtml: PropTypes.string,
    }),
    onRegenerate: PropTypes.func,
};

export default GapPredictionModal;
